"use client";

import type { MouseEvent } from "react";
import type { Locale } from "@/kernel/locale";
import { switchAddress } from "../../paths";
import { localeCookie } from "../../preference";
import "./language-switcher.css";

export type LanguageOption = {
  locale: Locale;
  /** Visible label, for example "EN". */
  short: string;
  /** The language's own name, read by screen readers. */
  name: string;
  /** BCP 47 language of the name. */
  lang: string;
  /** The page's clean address in this language. */
  href: string;
  /** The same address with ?lang=, which works without scripts (the proxy sets the cookie). */
  fallbackHref: string;
  current: boolean;
};

export type LanguageSwitcherProps = {
  label: string;
  options: readonly LanguageOption[];
  variant?: "bar" | "menu";
};

/**
 * Compact EN/UA switch (DSH-016). A click remembers the choice in the locale
 * cookie and opens the clean address, keeping the current query parameters
 * (other than lang) and section anchor.
 */
export function LanguageSwitcher({ label, options, variant = "bar" }: LanguageSwitcherProps) {
  const choose = (option: LanguageOption) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    document.cookie = localeCookie(option.locale, window.location.protocol === "https:");
    window.location.assign(switchAddress(option.href, window.location.search, window.location.hash));
  };
  return (
    <div className={`lang lang-${variant}`} role="group" aria-label={label}>
      {options.map((option) => (
        <a
          key={option.locale}
          href={option.fallbackHref}
          hrefLang={option.lang}
          lang={option.lang}
          aria-label={option.name}
          aria-current={option.current ? "true" : undefined}
          onClick={choose(option)}
        >
          {option.short}
        </a>
      ))}
    </div>
  );
}
